import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileText, Download, RefreshCw, BookOpen, Archive, TestTube } from "lucide-react";

/**
 * DocsTab — iter77 (iter87.103 — two-library restructure)
 *
 * Splits the docs viewer into two libraries:
 *   • Reference Docs — current, live, actively consulted
 *   • Legacy Docs    — retired / superseded / one-time snapshots
 *
 * Each library renders as a dropdown for cleaner navigation (was an
 * ever-growing button grid). Library tag comes from the backend
 * (`_DOC_LIBRARY` in docs_viewer.py) so add/move decisions live in one
 * place instead of split across FE + BE.
 */

const DOC_LABELS = {
    BRD: "Business Requirements",
    FRD: "Functional Requirements",
    TRD: "Technical Requirements",
    CHANGELOG: "Changelog",
    CHANGELOG_ARCHIVE: "Changelog Archive",
    ROADMAP: "Roadmap",
    PRD: "Product Requirements",
    NORTH_STAR: "North Star",
    VALUES_REGISTRY: "Values Registry",
    VALUES_REGISTRY_AUDIT: "Values Registry Audit",
    TCL_REGISTRY: "TCL Registry",
    TCL_MAPPING_PROPOSALS: "TCL Mapping Proposals",
    TCL_REGISTRY_AUDIT: "TCL Registry Audit",
    OBLIGATIONS_REGISTRY: "Obligations Registry",
    OBLIGATIONS_REGISTRY_AUDIT: "Obligations Registry Audit",
    GAME_HEALTH_REGISTRY: "Game Health Registry",
    FEATURE_REGISTRY: "Feature Registry",
    VENDOR_REGISTRY: "Vendor Registry",
    VENDOR_REGISTRY_AUDIT: "Vendor Registry Audit",
    PULSE_REGISTRY: "Pulse Registry",
    PULSE_DISPOSITIONS: "Pulse Dispositions",
    SECURITY_POSTURE_REGISTRY: "Security Posture Registry",
    SECURITY_POSTURE_REGISTRY_AUDIT: "Security Posture Audit",
    AGENT_OPERATING_AGREEMENT: "Agent Operating Agreement",
    AGENT_AGREEMENT_AUDIT: "Agent Agreement Audit",
    AGENT_LEARNINGS: "Agent Learnings",
    COMMONS_BRD: "Commons BRD",
    COMMONS_FRD: "Commons FRD",
    COMMONS_TRD: "Commons TRD",
    COMMONS_MAESTRO_CANON_REDESIGN: "Commons Maestro/Canon Redesign",
    MOBILE_TESTER_SCOPE: "Mobile Stand-In Scope",
    P4_MOBILE_IDENTITY: "P4 Mobile Unified Identity",
    RIVALRY_PRO_ISOLATION: "Rivalry Pro Isolation v1",
    TIER_DOWNGRADE_BEHAVIOR: "Tier Downgrade Behavior",
    TAXONOMY: "Taxonomy Map (governance web)",
    ARCHITECTURE: "Architecture & Phase B",
    STARTER_KIT: "Starter Kit",
    REPO_STRUCTURE: "Repo Structure",
    WORKFLOWS_INDEX: "Workflows Index",
    MEMORY_CONTENTS: "Memory Contents Index",
    GITHUB_PAT_ROTATION: "GitHub PAT Rotation",
    KEY_ROTATION_RUNBOOK: "Key Rotation Runbook",
    PRO_PLUS_USER_DELETE_RUNBOOK: "Pro+ User Delete Runbook",
    PRO_PLUS_V1_SCOPE: "Pro+ v1 — Scope",
    LIVE_DRAFT_HIDDEN: "Live Draft — Hidden Feature Memo",
    DEPLOY_CHECKLIST: "Deploy Checklist",
    RELEASE_OPS_SETUP: "Release Ops Setup",
    TESTING_PLAYBOOK: "Testing Playbook",
    NEW_GAME_TEMPLATE_PLAYBOOK: "New Game Template Playbook",
    TECH_DEBT: "Tech Debt",
    TECH_DEBT_ROUTINE: "Tech Debt Routine",
    PROCESS_FAILURES: "Process Failures",
    CLERK_VS_DIY: "Clerk vs. DIY — Operator Experience",
    COMPLIANCE_OPS_V1_SCOPE: "Compliance Ops v1 — Scope",
    ITER85_VS_87_70: "iter85 vs iter87.70 — Comparison",
    // iter87.199.98 — Vendor Test Procedures library.
    CURRENT_STATE: "Current State — Testing Workflows Index",
    VENDOR_TEST_LEMONSQUEEZY: "Billing / Subscription — Lemon Squeezy",
};

const LIBRARIES = [
    {
        key: "reference",
        label: "Reference Docs",
        subtitle: "Current, live, actively consulted",
        icon: BookOpen,
        accent: "text-volt",
    },
    {
        // iter87.199.98 — Vendor Test Procedures. Copy-paste-ready
        // QA workflows per vendor integration. First entry is
        // Lemon Squeezy billing; Auth and Runtime slots planned.
        key: "vendor_test",
        label: "Vendor Test Procedures",
        subtitle: "Copy-paste QA workflows per integration",
        icon: TestTube,
        accent: "text-cyber",
    },
    {
        key: "legacy",
        label: "Legacy Docs",
        subtitle: "Retired · superseded · one-time snapshots · audits",
        icon: Archive,
        accent: "text-zinc-500",
    },
];

function _labelFor(key) {
    return DOC_LABELS[key] || key;
}

function _sortDocs(docs) {
    return [...docs].sort((a, b) => _labelFor(a.key).localeCompare(_labelFor(b.key)));
}

export default function DocsTab() {
    const [list, setList] = useState([]);
    const [active, setActive] = useState(null);
    const [body, setBody] = useState("");
    const [loadingList, setLoadingList] = useState(false);
    const [loadingBody, setLoadingBody] = useState(false);

    const loadList = useCallback(async () => {
        setLoadingList(true);
        try {
            const data = await api.listDocs();
            const docs = data.docs || [];
            setList(docs);
            // Default active = first reference doc
            if (docs.length > 0 && !active) {
                const firstRef = docs.find((d) => (d.library || "reference") === "reference") || docs[0];
                setActive(firstRef.key);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load docs");
        }
        setLoadingList(false);
    }, [active]);

    const loadBody = useCallback(async (key) => {
        if (!key) return;
        setLoadingBody(true);
        try {
            const text = await api.getDoc(key);
            setBody(typeof text === "string" ? text : (text?.toString?.() || ""));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load doc");
        }
        setLoadingBody(false);
    }, []);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => { if (active) loadBody(active); }, [active, loadBody]);

    const byLibrary = useMemo(() => {
        const grouped = { reference: [], legacy: [] };
        for (const d of list) {
            const lib = d.library || "reference";
            (grouped[lib] || (grouped[lib] = [])).push(d);
        }
        grouped.reference = _sortDocs(grouped.reference);
        grouped.legacy = _sortDocs(grouped.legacy);
        return grouped;
    }, [list]);

    const activeDoc = list.find((d) => d.key === active);
    const activeIsLegacy = activeDoc && activeDoc.library === "legacy";

    return (
        <div className="space-y-4" data-testid="docs-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                        <FileText className="w-6 h-6 text-volt" />
                        Docs
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1">
                        Source-of-truth markdown, split into <span className="text-volt">Reference</span> (current)
                        and <span className="text-zinc-400">Legacy</span> (retired) libraries. Read-only viewer —
                        edits happen in git alongside code to avoid drift.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={loadList}
                    disabled={loadingList}
                    className="brutal-btn-ghost text-xs py-1.5 px-2 disabled:opacity-30"
                    data-testid="docs-refresh-btn"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Two library dropdowns */}
            <div className="grid gap-3 sm:grid-cols-2">
                {LIBRARIES.map((lib) => {
                    const docs = byLibrary[lib.key] || [];
                    const IconEl = lib.icon;
                    const isActiveHere = docs.some((d) => d.key === active);
                    return (
                        <div
                            key={lib.key}
                            className="brutal-card p-3 space-y-2"
                            data-testid={`docs-library-${lib.key}`}
                        >
                            <div className="flex items-start gap-2">
                                <IconEl className={`w-4 h-4 mt-0.5 ${lib.accent}`} strokeWidth={2.5} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-black uppercase tracking-wider">
                                        {lib.label}
                                        <span className="ml-1.5 opacity-50 font-normal">({docs.length})</span>
                                    </div>
                                    <div className="text-[10px] text-zinc-500 mt-0.5">{lib.subtitle}</div>
                                </div>
                            </div>
                            <select
                                data-testid={`docs-select-${lib.key}`}
                                value={isActiveHere ? active : ""}
                                onChange={(e) => e.target.value && setActive(e.target.value)}
                                className="w-full text-xs bg-[#0d0d0d] border border-[#222] rounded px-2 py-1.5 focus:border-cyber focus:outline-none text-white"
                            >
                                <option value="" disabled>
                                    {docs.length === 0 ? "None yet" : `Choose a ${lib.label.toLowerCase().replace(" docs", "")} doc…`}
                                </option>
                                {docs.map((d) => (
                                    <option key={d.key} value={d.key}>
                                        {_labelFor(d.key)}  ({d.line_count} lines)
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                })}
            </div>

            {active && (
                <div className="brutal-card p-4" data-testid="docs-body-panel">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#222] gap-3 flex-wrap">
                        <div className="min-w-0">
                            <h3 className="font-display font-extrabold uppercase text-lg flex items-center gap-2">
                                {_labelFor(active)}
                                {activeIsLegacy && (
                                    <span
                                        className="text-[9px] uppercase font-normal tracking-wider px-1.5 py-0.5 border border-zinc-600 text-zinc-400 rounded"
                                        data-testid="docs-legacy-badge"
                                    >
                                        Legacy
                                    </span>
                                )}
                            </h3>
                            <p className="text-[10px] text-zinc-500 mt-0.5">
                                {activeDoc?.filename}
                            </p>
                        </div>
                        <a
                            href={api.docDownloadUrl(active)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`docs-download-${active}-btn`}
                            className="brutal-btn-ghost text-xs py-1.5 px-2"
                        >
                            <Download className="w-3.5 h-3.5 text-cyber" /> Download .md
                        </a>
                    </div>
                    {loadingBody ? (
                        <p className="text-xs text-zinc-500 italic">Loading…</p>
                    ) : (
                        <pre
                            data-testid="docs-body"
                            className="font-mono text-[11px] leading-relaxed text-zinc-300 whitespace-pre-wrap break-words max-h-[70dvh] overflow-y-auto bg-ink p-4 border border-[#222]"
                        >
                            {body}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
}
